import type { LucideIcon } from 'lucide-react';
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';
import { cx } from './cx';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'brand';
export type ButtonSize = 'sm' | 'md' | 'lg';

/** The button classes, for links that look like buttons (a router's `Link`). */
export function buttonClass(
  variant: ButtonVariant = 'secondary',
  size: ButtonSize = 'md',
  iconOnly = false,
) {
  return cx(
    'mz-btn',
    `mz-btn-${variant}`,
    size !== 'md' && `mz-btn-${size}`,
    iconOnly && 'mz-btn-icon',
  );
}

interface ButtonLook {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: LucideIcon;
  iconRight?: LucideIcon;
  /** Required when the button shows only an icon. */
  label?: string;
  children?: ReactNode;
}

/** The icon, text and right icon inside a button or a button-like link. */
export function ButtonContent({
  size = 'md',
  icon: Icon,
  iconRight: IconRight,
  loading,
  children,
}: Pick<ButtonLook, 'size' | 'icon' | 'iconRight' | 'children'> & { loading?: boolean }) {
  const iconSize = size === 'sm' ? 14 : 16;
  return (
    <>
      {loading ? (
        <span className="mz-spinner" aria-hidden="true" />
      ) : (
        Icon && <Icon size={iconSize} strokeWidth={1.75} aria-hidden="true" className="mz-icon" />
      )}
      {children != null && <span>{children}</span>}
      {IconRight && (
        <IconRight size={iconSize} strokeWidth={1.75} aria-hidden="true" className="mz-icon" />
      )}
    </>
  );
}

export function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  iconRight,
  label,
  loading,
  className,
  children,
  disabled,
  ...button
}: ButtonLook & { loading?: boolean } & ButtonHTMLAttributes<HTMLButtonElement>) {
  const iconOnly = Boolean(icon) && children == null;
  return (
    <button
      type="button"
      className={cx(buttonClass(variant, size, iconOnly), className)}
      aria-label={iconOnly ? label : undefined}
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      {...button}
    >
      <ButtonContent size={size} icon={icon} iconRight={iconRight} loading={loading}>
        {children}
      </ButtonContent>
    </button>
  );
}

/** A plain link styled as a button (another site, or a page outside the router). */
export function LinkButton({
  variant = 'secondary',
  size = 'md',
  icon,
  iconRight,
  label,
  className,
  children,
  href,
  ...anchor
}: ButtonLook & { href: string } & AnchorHTMLAttributes<HTMLAnchorElement>) {
  const iconOnly = Boolean(icon) && children == null;
  return (
    <a
      href={href}
      className={cx(buttonClass(variant, size, iconOnly), className)}
      aria-label={iconOnly ? label : undefined}
      {...anchor}
    >
      <ButtonContent size={size} icon={icon} iconRight={iconRight}>
        {children}
      </ButtonContent>
    </a>
  );
}
